import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/Base/Dialog";
import { Card, Employee, Shop } from "@/types/v2";
import { ChartColumn } from "lucide-react";
import { Fragment, ReactNode, useEffect, useMemo, useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/Base/Tooltip";
import { Button } from "@/components/Base/Button";
import { firestoreToDateTime } from "@/lib/utils";
import { getCardSection } from "@/lib/cards";
import { getEmployeeName } from "@/lib/employees";
import { JobHourDisplay } from "@/components/Dashboard/components/Card/JobHourDisplay";
import { CardMetricsReport } from "@/components/Dashboard/components/Card/CardMetricsReport";
import { useFeatures } from "@/hooks/useFeatures";
import { Icon } from "@/components/Common/Icon";
import { useStore } from "@/stores/useStore";
import { TagsList } from "@/components/Common/TagsList";

export interface CardMetricsProps {
  cardData: Card;
  fullButton?: boolean;
  trigger?: ReactNode;
  externalTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  employees?: Employee[];
  shop: Shop;
}

export const CardMetrics = ({
  cardData,
  fullButton,
  trigger,
  externalTrigger,
  open,
  onOpenChange,
  employees,
  shop,
}: CardMetricsProps) => {
  const [isOpen, setIsOpen] = useState(open ?? false);
  const { tags } = useStore();

  const {
    features: { isManualCardOnlyEnabled },
  } = useFeatures(shop);
  const isManualCardLaborEnabled = useMemo(
    () => shop?.cardConfig?.cardContents?.manualJobHoursEnabled ?? false,
    [shop]
  );

  // Sync local state with the controlled `open` prop
  useEffect(() => {
    if (typeof open === "boolean") {
      setIsOpen(open);
    }
  }, [open]);

  // Handle the dialog's open state changes
  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    onOpenChange?.(newOpen); // Notify parent if `onOpenChange` is provided
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={isOpen}>
      {trigger && !externalTrigger ? (
        // Use custom trigger if provided
        <DialogTrigger asChild>{trigger}</DialogTrigger>
      ) : (
        // Default trigger button
        !externalTrigger && (
          <DialogTrigger asChild={fullButton}>
            {fullButton ? <Button>Card Metrics</Button> : <ChartColumn />}
          </DialogTrigger>
        )
      )}
      <DialogContent className="m-0 flex max-h-screen flex-col gap-1 p-0">
        <DialogTitle>
          {/* Header */}
          <div
            className="flex h-10 w-full items-center justify-center rounded-t-lg text-primary"
            style={{ backgroundColor: cardData.color }}
          >
            <JobHourDisplay
              totalLaborHours={cardData.totalLaborHours}
              className="text-base"
              iconSize={20}
              isManualCardOnly={isManualCardOnlyEnabled}
              isManualJobHoursEnabled={isManualCardLaborEnabled}
            />
          </div>
        </DialogTitle>

        {/* Scrollable Content */}
        <div className="w-full flex-1 overflow-y-auto p-4">
          <TooltipProvider key={"update"}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="text-3xl font-bold">
                  {getCardSection(cardData, shop, "primaryField")}
                </div>
              </TooltipTrigger>
              <TooltipContent>{`RO ID: ${cardData?.id}`}</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <div className="text-2xl">
            {getCardSection(cardData, shop, "secondaryField")}
          </div>
          <div className="text-xl">
            {getCardSection(cardData, shop, "tertiaryField")}
          </div>

          <div className="mt-4 border-t text-xl font-bold"></div>

          <div className="mt-4 flex grid-cols-3 flex-row">
            <div className="flex flex-row items-center gap-2">
              <Icon name={"appointment"} size={16} />
              Appointment:{" "}
              {cardData?.appointment?.startAt
                ? firestoreToDateTime(
                    cardData?.appointment?.startAt
                  )?.toLocaleString()
                : "No appointment set"}
              {cardData?.appointment?.endAt &&
                cardData?.appointment?.startAt &&
                " - "}
              {firestoreToDateTime(
                cardData?.appointment?.endAt
              )?.toLocaleString()}
            </div>
          </div>
          <div className="flex grid-cols-3 flex-row">
            <div className="flex flex-row items-center gap-2">
              <Icon name={"dueOut"} size={16} />
              Due Date:{" "}
              {firestoreToDateTime(cardData.promisedTime)?.toLocaleString() ??
                "No due date set"}
            </div>
          </div>

          <div className="flex w-full grid-cols-2 flex-row">
            {cardData?.label && (
              <div className="flex flex-row items-center gap-2">
                <Icon name={"tag"} size={16} />
                Label:
                <div
                  className="flex justify-center rounded-lg px-2 font-bold text-white"
                  style={{ backgroundColor: cardData?.backgroundColor }}
                >
                  {cardData?.label}
                </div>
              </div>
            )}

            <div className="flex flex-row items-center gap-2">
              <Icon name={"tags"} size={16} />
              Tags:
              <TagsList tags={cardData?.tags ?? []} allTags={tags?.tags} />
            </div>
          </div>

          <div className="flex grid-cols-3 flex-row">
            <div className="flex flex-row items-center gap-2">
              <Icon name={"waiter"} size={16} />
              Waiter: {cardData?.isWaiter ? "Yes" : "No"}
            </div>
          </div>

          {(cardData?.technicianId !== "none" ||
            cardData?.serviceWriterId !== "none") && (
            <Fragment>
              <div className="mt-4 border-t text-xl font-bold"></div>

              <div className="mt-1 grid grid-cols-2">
                <div className="flex flex-col gap-1">
                  <div className="font-bold">Technician</div>
                  <div>
                    {getEmployeeName(cardData?.technicianId, employees ?? [])}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <div className="font-bold">Service Advisor</div>
                  <div>
                    {getEmployeeName(
                      cardData?.serviceWriterId,
                      employees ?? []
                    )}
                  </div>
                </div>
              </div>
            </Fragment>
          )}

          <CardMetricsReport cardData={cardData} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
